import { useMutation, useQuery, useLazyQuery } from "@apollo/client/react";
import { Select, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { CreationForm } from "../../../components/form/CreationForm";
import { PageHeader } from "../../../components/PageHeader";
import { FormSkeleton } from "../../../components/SkeletonLoader";
import {
  CREATE_PRESET_SCRIPT_MUTATION,
  UPDATE_PRESET_SCRIPT_MUTATION,
} from "../steps/rules/graphql/mutations";
import {
  BUILTIN_PRESETS_QUERY,
  BUILTIN_PRESET_QUERY,
  PRESET_SCRIPTS_QUERY,
  PRESET_SCRIPT_QUERY,
} from "../steps/rules/graphql/queries";
import {
  BuiltinPresetSummaryForSelect,
  groupPresetsForSelect,
} from "../steps/rules/builtin-preset.constants";
import {
  PRESET_PARAMETER_DEFAULT_MAX_LENGTH,
  PRESET_PARAMETER_DESCRIPTION_MAX_LENGTH,
  PRESET_PARAMETER_MAX_COUNT,
  PRESET_PARAMETER_NAME_MAX_LENGTH,
  PresetParameterDraft,
  PresetParameterType,
  PresetParametersEditor,
} from "./PresetParametersEditor";

const PRESET_SCRIPTS_LIST_PATH = "/tournaments/preset-scripts/list";
const PRESET_NAME_MIN_LENGTH = 3;
const PRESET_NAME_MAX_LENGTH = 120;
const PRESET_DESCRIPTION_MAX_LENGTH = 2000;
const NOTIFICATION_ERROR_MAX_LENGTH = 200;
const PRESET_PARAMETER_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

interface PresetScriptDetailData {
  presetScript: {
    id: string;
    name: string;
    description: string | null;
    script: string;
    parameters: PresetParameterDraft[] | null;
  };
}

interface CreatePresetScriptVariables {
  input: {
    name: string;
    description?: string | null;
    script: string;
    parameters?: PresetParameterDraft[];
  };
}

interface UpdatePresetScriptVariables {
  id: string;
  input: {
    name?: string;
    description?: string | null;
    script?: string;
    parameters?: PresetParameterDraft[];
  };
}

interface BuiltinPresetsListResult {
  builtinPresets: BuiltinPresetSummaryForSelect[];
}

interface BuiltinPresetDetail {
  id: string;
  displayName: string;
  description: string;
  source: string;
  parameters: PresetParameterDraft[];
}

interface BuiltinPresetDetailResult {
  builtinPreset: BuiltinPresetDetail;
}

const PARAMETER_TYPES: PresetParameterType[] =
  Object.values(PresetParameterType);

const parametersSchema = yup
  .array()
  .of(
    yup.object({
      name: yup
        .string()
        .required("Parameter name is required")
        .matches(
          PRESET_PARAMETER_NAME_PATTERN,
          "Use letters, digits and underscores only (must start with a letter or underscore)",
        )
        .max(PRESET_PARAMETER_NAME_MAX_LENGTH, "Parameter name too long"),
      type: yup
        .string()
        .oneOf(PARAMETER_TYPES, "Invalid parameter type")
        .required(),
      required: yup.boolean().required(),
      defaultValue: yup
        .string()
        .max(PRESET_PARAMETER_DEFAULT_MAX_LENGTH)
        .nullable(),
      description: yup
        .string()
        .max(PRESET_PARAMETER_DESCRIPTION_MAX_LENGTH)
        .nullable(),
    }),
  )
  .max(
    PRESET_PARAMETER_MAX_COUNT,
    `Maximum ${PRESET_PARAMETER_MAX_COUNT} parameters allowed`,
  )
  .test("unique-names", "Parameter names must be unique", (value) => {
    if (!value) return true;
    const names = value
      .map((p) => p?.name)
      .filter((name): name is string => Boolean(name));
    return new Set(names).size === names.length;
  })
  .default([]);

const sanitizeParameters = (
  parameters: PresetParameterDraft[] | null | undefined,
): PresetParameterDraft[] =>
  (parameters ?? []).map((param) => ({
    name: param.name.trim(),
    type: param.type,
    required: param.required === true,
    defaultValue:
      param.defaultValue === null || param.defaultValue === ""
        ? null
        : param.defaultValue,
    description:
      param.description === null || param.description === ""
        ? null
        : param.description,
  }));

export const PresetScriptForm = () => {
  const navigate = useNavigate();
  const presetScriptId = useParams().presetScriptId;
  const isEditMode = !!presetScriptId;

  const { data, loading } = useQuery<PresetScriptDetailData>(
    PRESET_SCRIPT_QUERY,
    {
      variables: { id: presetScriptId ?? "" },
      skip: !presetScriptId,
    },
  );

  const [createPresetScript] = useMutation<
    { createPresetScript: { id: string } },
    CreatePresetScriptVariables
  >(CREATE_PRESET_SCRIPT_MUTATION, {
    refetchQueries: [{ query: PRESET_SCRIPTS_QUERY }],
  });
  const [updatePresetScript] = useMutation<
    { updatePresetScript: { id: string } },
    UpdatePresetScriptVariables
  >(UPDATE_PRESET_SCRIPT_MUTATION, {
    refetchQueries: [{ query: PRESET_SCRIPTS_QUERY }],
  });

  if (isEditMode && loading) {
    return (
      <div className="wp-fade-in">
        <PageHeader
          title="Edit Preset Script"
          showBackButton
          showBreadcrumbs={false}
        />
        <FormSkeleton fields={4} />
      </div>
    );
  }

  if (isEditMode && !data?.presetScript) {
    return (
      <div className="wp-fade-in">
        <PageHeader
          title="Edit Preset Script"
          showBackButton
          showBreadcrumbs={false}
        />
        <Text>Preset script not found.</Text>
      </div>
    );
  }

  return (
    <PresetScriptFormBody
      initialData={data?.presetScript ?? null}
      isEditMode={isEditMode}
      presetScriptId={presetScriptId}
      onCreate={async (variables) => {
        const result = await createPresetScript({ variables });
        if (result.error) {
          throw result.error;
        }
      }}
      onUpdate={async (variables) => {
        const result = await updatePresetScript({ variables });
        if (result.error) {
          throw result.error;
        }
      }}
      onSuccess={() => navigate(PRESET_SCRIPTS_LIST_PATH)}
    />
  );
};

type PresetScriptLoaded = PresetScriptDetailData["presetScript"];

interface PresetScriptFormBodyProps {
  initialData: PresetScriptLoaded | null;
  isEditMode: boolean;
  presetScriptId: string | undefined;
  onCreate: (variables: CreatePresetScriptVariables) => Promise<void>;
  onUpdate: (variables: UpdatePresetScriptVariables) => Promise<void>;
  onSuccess: () => void;
}

const PresetScriptFormBody = ({
  initialData,
  isEditMode,
  presetScriptId,
  onCreate,
  onUpdate,
  onSuccess,
}: PresetScriptFormBodyProps) => {
  const [name, setName] = useState<string>(initialData?.name ?? "");
  const [description, setDescription] = useState<string>(
    initialData?.description ?? "",
  );
  const [script, setScript] = useState<string>(initialData?.script ?? "");
  const [parameters, setParameters] = useState<PresetParameterDraft[]>(
    initialData?.parameters ?? [],
  );

  // Force-remount the CreationForm when a template prefills the values so that
  // RHF picks up the new defaultValues for fields whose internal state would
  // otherwise stay at the original initialData.
  const [formKey, setFormKey] = useState<number>(0);

  const builtinList = useQuery<BuiltinPresetsListResult>(BUILTIN_PRESETS_QUERY, {
    skip: isEditMode,
  });

  const [fetchBuiltinTemplate, fetchBuiltinTemplateState] =
    useLazyQuery<BuiltinPresetDetailResult>(BUILTIN_PRESET_QUERY);

  const [pickedTemplateId, setPickedTemplateId] = useState<string | null>(null);

  // Guards against out-of-order template loads: if the user switches templates
  // while a fetch is in flight, only the most recent selection should apply.
  const latestTemplateRequestIdRef = useRef<number>(0);

  // Mantine's combobox parser calls `.items.map(...)` on each group, so
  // flat `{ value, label, group }` items crash at render time.
  const templateOptions = useMemo(
    () => groupPresetsForSelect(builtinList.data?.builtinPresets ?? []),
    [builtinList.data?.builtinPresets],
  );

  const handlePickTemplate = async (id: string | null) => {
    setPickedTemplateId(id);
    if (!id) return;
    const requestId = latestTemplateRequestIdRef.current + 1;
    latestTemplateRequestIdRef.current = requestId;
    try {
      const result = await fetchBuiltinTemplate({ variables: { id } });
      if (requestId !== latestTemplateRequestIdRef.current) {
        // A newer template pick superseded this one — drop the stale result.
        return;
      }
      if (result.error) {
        throw result.error;
      }
      const template = result.data?.builtinPreset;
      if (!template) {
        throw new Error("Template not found");
      }
      setName(template.displayName);
      setDescription(template.description);
      setScript(template.source);
      setParameters(template.parameters);
      setFormKey((prev) => prev + 1);
      notifications.show({
        title: "Template loaded",
        message: `Started from "${template.displayName}". Edit and save to make it yours.`,
        color: "blue",
        autoClose: 3000,
      });
    } catch (error) {
      if (requestId !== latestTemplateRequestIdRef.current) {
        // A newer pick is in flight — let it drive the UI state and notifications.
        return;
      }
      // Reset picker so the user can retry the same template or pick another
      // without the Select showing a stale selection that never loaded.
      setPickedTemplateId(null);
      notifications.show({
        title: "Failed to load template",
        message:
          error instanceof Error ? error.message : "An unknown error occurred",
        color: "red",
      });
    }
  };

  return (
    <div className="wp-fade-in">
      <PageHeader
        title={isEditMode ? "Edit Preset Script" : "Create Preset Script"}
        showBackButton
        showBreadcrumbs={false}
      />
      <Stack gap="md">
        {!isEditMode && (
          <Select
            label="Start from a template (optional)"
            description="Loads the template's source, parameters, name, and description into the form. You can edit everything before saving."
            placeholder="Blank script"
            data={templateOptions}
            value={pickedTemplateId}
            onChange={handlePickTemplate}
            disabled={builtinList.loading || fetchBuiltinTemplateState.loading}
            searchable
            clearable
          />
        )}
        <CreationForm
          key={formKey}
          title={
            isEditMode
              ? `Edit "${initialData?.name ?? ""}"`
              : "New preset script"
          }
          schemaDefinition={{
            name: {
              type: "INPUT",
              label: "Name",
              yupConfig: yup
                .string()
                .required("Name is required")
                .min(
                  PRESET_NAME_MIN_LENGTH,
                  `Name must be at least ${PRESET_NAME_MIN_LENGTH} characters`,
                )
                .max(PRESET_NAME_MAX_LENGTH, "Name too long"),
              inputType: "text",
              placeholder: "Single Elimination 16",
              defaultValue: name,
              value: name,
              onChange: (next) => setName(next),
            },
            description: {
              type: "TEXTAREA",
              label: "Description",
              yupConfig: yup
                .string()
                .max(PRESET_DESCRIPTION_MAX_LENGTH)
                .nullable(),
              defaultValue: description,
              value: description,
              minRows: 2,
              maxRows: 6,
              onChange: (next) => setDescription(next),
            },
            script: {
              type: "CODE_EDITOR",
              label: "Lua script",
              yupConfig: yup.string().required("Script is required"),
              language: "lua",
              height: "400px",
              defaultValue: script,
              value: script,
              onChange: (next) => setScript(next),
            },
            parameters: {
              type: "DYNAMIC",
              label: "Parameters",
              yupConfig: parametersSchema,
              defaultValue: parameters,
              element: (onChange) => (
                <PresetParametersEditor
                  value={parameters}
                  onChange={(next) => {
                    setParameters(next);
                    onChange(next);
                  }}
                />
              ),
            },
          }}
          onSubmit={async () => {
            const sanitizedParameters = sanitizeParameters(parameters);
            const trimmedName = name.trim();
            const trimmedDescription = description.trim();
            const finalDescription =
              trimmedDescription === "" ? null : trimmedDescription;

            try {
              if (isEditMode && presetScriptId) {
                await onUpdate({
                  id: presetScriptId,
                  input: {
                    name: trimmedName,
                    description: finalDescription,
                    script,
                    parameters: sanitizedParameters,
                  },
                });

                notifications.show({
                  title: "Preset updated",
                  message: `Preset "${trimmedName}" has been updated`,
                  color: "green",
                  autoClose: 3000,
                });
              } else {
                await onCreate({
                  input: {
                    name: trimmedName,
                    description: finalDescription,
                    script,
                    parameters: sanitizedParameters,
                  },
                });

                notifications.show({
                  title: "Preset created",
                  message: `Preset "${trimmedName}" has been created`,
                  color: "green",
                  autoClose: 3000,
                });
              }

              onSuccess();
            } catch (error) {
              notifications.show({
                title: isEditMode ? "Update failed" : "Creation failed",
                message:
                  error instanceof Error &&
                  error.message.length < NOTIFICATION_ERROR_MAX_LENGTH
                    ? error.message
                    : isEditMode
                      ? "Failed to update preset script"
                      : "Failed to create preset script",
                color: "red",
                autoClose: 5000,
              });
            }
          }}
        />
      </Stack>
    </div>
  );
};
